import React, { useState, useEffect } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import config from "../../config";           // ← adapter selon votre projet
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Eye,
  EyeOff,
  BookOpen,
  Users,
  Target,
  Sparkles,
  Key
} from "lucide-react";
import { AnimatedBackground } from "../../Components/animated-background"; // ← si vous l'utilisez
import { Link } from "react-router-dom";


export default function Login() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { register, handleSubmit } = useForm();

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Deux états différents pour deux popups distincts
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false); 

  // Fonction pour fermer le popup
  const handleCloseForgotModal = () => {
    setShowForgotModal(false);
  };
  const direction = i18n.language === "ar" ? "rtl" : "ltr";

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      localStorage.removeItem("accessToken");
      const response = await axios.post(`${config.SITE_API_URL}/login_customer`, data,
        { headers: { 'x-api-key': config.SITE_API_KEY } }
      );

      localStorage.setItem("isconnected", JSON.stringify(true));
      localStorage.setItem("accessToken", response.data.accesstoken);

      toast.success(t("success_login"));
       setTimeout(() => {
        navigate("/dashboard");
      }, 800);
    } catch (error) {
      const message = error.response?.data?.message || t("error_login");
const isActive = error.response?.data?.isActive; // Récupérer isActive de la réponse
    
    // → On ouvre le popup BLOQUÉ uniquement si isActive est "false"
    if (isActive === "false" || isActive === false) {
      setShowBlockedModal(true);
    } else {
        toast.error(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      dir={direction}
      className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-white flex items-center justify-center p-4 min-md:p-8 overflow-hidden relative"
    >
      {/* Fond animé (optionnel) */}
      <AnimatedBackground />

      {/* Bulles décoratives légères (optionnelles) */}
      <div className="absolute top-10 left-10 w-32 h-32 bg-blue-200 rounded-full opacity-20 animate-float" />
      <div
        className="absolute bottom-20 right-10 w-40 h-40 bg-orange-200 rounded-full opacity-20 animate-float"
        style={{ animationDelay: "1.2s" }}
      />
      <div
        className="absolute top-1/2 left-1/3 w-24 h-24 bg-cyan-200 rounded-full opacity-20 animate-float"
        style={{ animationDelay: "2.4s" }}
      />

      {/* <SideButtons />  ← décommente si tu veux garder */}

      <ToastContainer
        position={direction === "rtl" ? "bottom-right" : "bottom-left"}
        theme="colored"
      />

      <div className="relative z-10 w-full max-w-5xl">
        <div className="grid min-md:grid-cols-2 gap-8 min-md:gap-12 items-center">
          {/* Partie GAUCHE – Présentation (desktop seulement) */}
          <div className="hidden min-md:block">
            <div className="space-y-8">

              {/* Slogan */}
              <div className="space-y-3">
                <h2 className="text-4xl min-md:text-5xl font-bold text-blue-900 leading-tight">
                  {t("welcome_back_learning")}
                </h2>
                <p className="text-lg text-blue-700">
                  {t("connect_to_continue_professional_training")}
                </p>
              </div>

              {/* Features */}
              <div className="space-y-6 pt-4">
                <div className="flex gap-4 items-start">
                  <div className="w-11 h-11 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-orange-400/50">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-blue-900 text-lg">{t("personalized_paths")}</h3>
                    <p className="text-sm text-blue-700">{t("courses_adapted_to_your_profile")}</p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-11 h-11 bg-gradient-to-br from-cyan-400 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-400/50">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-blue-900 text-lg">{t("qualified_trainers")}</h3>
                    <p className="text-sm text-blue-700">{t("learn_from_professionals")}</p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-11 h-11 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-600/50">
                    <BookOpen className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-blue-900 text-lg">{t("practical_content")}</h3>
                    <p className="text-sm text-blue-700">{t("exercises_projects_certificates")}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Partie DROITE – Formulaire */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 min-md:p-10 border border-blue-100 backdrop-blur-sm bg-opacity-95">
            <div className="space-y-8">
              <div className="space-y-2 text-center min-md:text-left">
                <h2 className="text-3xl font-bold text-blue-900 flex items-center justify-center min-md:justify-start gap-2">
                  {t("login")}
                  <Sparkles className="w-6 h-6 text-orange-400 animate-pulse" />
                </h2>
                <p className="text-blue-600">{t("sign_in_to_your_account")}</p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Téléphone */}
                <div className="space-y-2">
                  <label htmlFor="parent_phone" className="block text-sm font-semibold text-blue-900">
                    {t("parent_phone")} <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="parent_phone"
                    type="text"
                    {...register("parent_phone")}
                    placeholder={t("placeholders.parent_phone")}
                    className="w-full px-4 py-3.5 rounded-xl border-2 border-blue-200 bg-blue-50 focus:outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-200 transition-all duration-300 text-blue-900 placeholder:text-blue-400 hover:border-blue-300"
                    required
                  />
                </div>

                {/* Mot de passe */}
                <div className="space-y-2">
                  <label htmlFor="password" className="block text-sm font-semibold text-blue-900">
                    {t("password")} <span className="text-red-600">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      {...register("password")}
                      placeholder="••••••••"
                      className="w-full px-4 py-3.5 rounded-xl border-2 border-blue-200 bg-blue-50 focus:outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-200 transition-all duration-300 text-blue-900 placeholder:text-blue-400 hover:border-blue-300 pr-12"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`absolute ${direction === "rtl" ? "left-4" : "right-4"} top-1/2 -translate-y-1/2 text-blue-600 hover:text-blue-900 transition-all duration-300 hover:scale-125`}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>

                  {/* Lien Mot de passe oublié */}
                  <div className="flex items-center justify-end text-sm mt-1">
                    <button
                    type="button"
              onClick={() => setShowForgotModal(true)}
                      className={`text-cyan-600 hover:text-cyan-800 font-medium transition-colors hover:underline ${direction === "rtl" ? "mr-2 text-right" : "ml-2"}`}
                    >
                      {t("forgot_password")}
                    </button>
                  </div>
                </div>

                {/* Bouton Connexion */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-blue-900 to-blue-700 hover:from-blue-800 hover:to-blue-600 text-white font-semibold py-3.5 rounded-xl transition-all duration-300 transform hover:scale-[1.03] active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg hover:shadow-xl hover:shadow-blue-500/50 relative overflow-hidden group mt-4"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 animate-shimmer" />
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-3 relative z-10">
                      <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {t("connecting")}...
                    </span>
                  ) : (
                    <span className="relative z-10">{t("connect")}</span>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* ─── POPUP COMPTE BLOQUÉ ────────────────────────────────────────────── */}
      {showBlockedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center relative">
            <button
              onClick={() => setShowBlockedModal(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 text-2xl leading-none"
            >
              ×
            </button>

            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-10 h-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-3">
              {t("account_blocked_title") || "Compte bloqué"}
            </h3>

            <p className="text-gray-600 mb-8 leading-relaxed">
              {t("account_blocked_message") ||
                "Votre compte est actuellement désactivé.\nVeuillez contacter l'administration pour le réactiver."}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setShowBlockedModal(false)}
                className="px-6 py-3 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 transition font-medium"
              >
                {t("close") || "Fermer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── POPUP MOT DE PASSE OUBLIÉ (nouveau) ────────────────────────────── */}
      {showForgotModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center relative">
            <button
              onClick={handleCloseForgotModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 text-2xl leading-none"
            >
              ×
            </button>

            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
              <Key className="w-10 h-10 text-amber-600" />
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-3">
              {t("forgot_password_title") || "Mot de passe oublié ?"}
            </h3>

            <p className="text-gray-600 mb-8 leading-relaxed">
              {t("forgot_password_message") ||
                "Veuillez contacter l'administration pour réinitialiser votre mot de passe."}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
               onClick={handleCloseForgotModal}
                className="px-6 py-3 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 transition font-medium"
              >
                {t("close") || "Fermer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}